
// AUTO-GENERATED SDK for HeldShareRebalanceLine
import { Injectable } from '@angular/core';
import { GraphqlApiService } from '../../api/graphql-api.service';
import { HeldShareRebalanceLineBuilder } from './held-share-rebalance-line.builder';
import { HeldShareRebalanceLineSql } from './held-share-rebalance-line.sqlbuilder';
import type { ApiResponse } from '../../api/types';
import type { HeldShareRebalanceLinePoco } from '@libs/graphql/models';

@Injectable({ providedIn:'root' })
export class HeldShareRebalanceLineSdk {

  constructor(private api: GraphqlApiService) {}

  // ============================================================
  // SAFE JSON
  // ============================================================
  private safeJSON(v: any) {
    try { return JSON.stringify(v); }
    catch { return '{}'; }
  }

  // ============================================================
  // SAFE RESPONSE WRAPPER
  // ============================================================
  private wrap<T>(raw: any, key: string): ApiResponse<T> {
    const node = raw[key] ?? {};
    return {
      success: node.success ?? false,
      message: node.message ?? '',
      error: node.error ?? '',
      totalRecord: node.totalRecord ?? 0,
      data: (node.data ?? []) as T
    };
  }

  // ============================================================
  // SAFE SELECT
  // ============================================================
  private safeSelect(fields: string[]) {
    return fields.length
      ? fields.map(f => ({ columnName: f, alias: f, isTrusted: true }))
      : [{ columnName: '*', alias: '*', isTrusted: true }];
  }

  // ============================================================
  // FILTER BUILDER
  // ============================================================
  private cf(f: any): any[] {
    if (!f || typeof f !== 'object') return [];

    if (f.AND)
      return [{
        logic: 'And',
        isTrusted: false,
        subFilters: f.AND.map((x: any) => this.cf(x)[0])
      }];

    if (f.OR)
      return [{
        logic: 'Or',
        isTrusted: false,
        subFilters: f.OR.map((x: any) => this.cf(x)[0])
      }];

    const opMap: any = {
      eq: '=', ne: '<>', like: 'LIKE',
      gt: '>', gte: '>=', lt: '<', lte: '<=',
      in: 'IN', notIn: 'NOT IN'
    };

    const rows: any[] = [];

    for (const k of Object.keys(f)) {
      const v = f[k];

      if (typeof v !== 'object') {
        rows.push({
          column: k,
          operator: '=',
          value: v,
          logic: 'And',
          subFilters: [],
          isTrusted: false
        });
      } else {
        const op = Object.keys(v)[0];
        rows.push({
          column: k,
          operator: opMap[op] ?? op,
          value: v[op],
          logic: 'And',
          subFilters: [],
          isTrusted: false
        });
      }
    }

    return rows;
  }

  // ============================================================
  // LIST MODE
  // ============================================================
  async list(
    filter: any = {},
    sorts: any[] = [],
    fields: string[] = [],
    page = 1,
    pageSize = 50,
    options: { strict?: boolean, useCache?: boolean, debug?: boolean } = {}
  ): Promise<ApiResponse<HeldShareRebalanceLinePoco[]>> {

    const gql = `query ListHeldShareRebalanceLine($input: DynamicQueryJsonInput) {
      listHeldShareRebalanceLine(input: $input) {
        success
        message
        error
        totalRecord
        data {
          ${fields.length ? fields.join('\n          ') : 'id'}
        }
      }
    }`;

    const where = this.cf(filter);
    const selects = this.safeSelect(fields);
    const safeSorts = sorts && sorts.length
      ? sorts
      : [{ column: 'id', desc: true, isTrusted: true }];

    const raw = await this.api.request<any>(gql, {
      input: {
        queryJson: this.safeJSON({
          baseTable: 'HeldShareRebalanceLine',
          baseAlias: 'heldShareRebalanceLine',
          selects,
          joins: [],
          where,
          having: [],
          sorts: safeSorts,
          groupBy: [],
          skip: (page - 1) * pageSize,
          take: pageSize,
          options: {
            strictMode: options.strict ?? false,
            useCache: options.useCache ?? false
          }
        })
      }
    });

    return this.wrap<HeldShareRebalanceLinePoco[]>(raw, 'listHeldShareRebalanceLine');
  }

  // ============================================================
  // QUERY MODE (GraphQL builder)
  // ============================================================
  async query(
    b: (q: HeldShareRebalanceLineBuilder) => HeldShareRebalanceLineBuilder,
    filter: any = {},
    sorts: any[] = [],
    page = 1,
    pageSize = 50,
    options: { strict?: boolean } = {}
  ): Promise<ApiResponse<HeldShareRebalanceLinePoco[]>> {

    const q = new HeldShareRebalanceLineBuilder();
    b(q);

    const selects = q.getSelections().map(x => ({
      columnName: x,
      alias : x,
      isTrusted: true
    }));

    const gql = `query QHeldShareRebalanceLine($input: DynamicQueryJsonInput) {
      listHeldShareRebalanceLine(input: $input) {
        success
        message
        error
        totalRecord
        data
          ${q.toSelectionSet()}
      }
    }`;

    const raw = await this.api.request<any>(gql, {
      input: {
        queryJson: this.safeJSON({
          baseTable: 'HeldShareRebalanceLine',
          baseAlias: 'heldShareRebalanceLine',
          selects,
          joins: [],
          where: this.cf(filter),
          having: [],
          sorts,
          groupBy: [],
          skip: (page - 1) * pageSize,
          take: pageSize,
          options: { strictMode: options.strict ?? false }
        })
      }
    });

    return this.wrap<HeldShareRebalanceLinePoco[]>(raw, 'listHeldShareRebalanceLine');
  }

  // ============================================================
  // SQL MODE (SQL AST)
  // ============================================================
  async sql(
    fn: (q: HeldShareRebalanceLineSql) => void | HeldShareRebalanceLineSql,
    options: { strict?: boolean, debug?: boolean } = {}
  ): Promise<ApiResponse<HeldShareRebalanceLinePoco[]>> {

    const instance = new HeldShareRebalanceLineSql();
    const result = fn(instance) || instance;
    const q = result.build();

    q.options = q.options ?? {};
    q.options.strictMode = options.strict ?? false;

    const gql = `query SqlHeldShareRebalanceLine($input: DynamicQueryJsonInput) {
      listHeldShareRebalanceLine(input: $input) {
        success
        message
        error
        totalRecord
        data { id }
      }
    }`;

    const raw = await this.api.request<any>(gql, {
      input: { queryJson: this.safeJSON(q) }
    });

    return this.wrap<HeldShareRebalanceLinePoco[]>(raw, 'listHeldShareRebalanceLine');
  }

  // ============================================================
  // SAVE
  // ============================================================
  async save(item: any): Promise<ApiResponse<HeldShareRebalanceLinePoco>> {
    const gql = `mutation SaveHeldShareRebalanceLine($input: UpsertInputOfHeldShareRebalanceLinePocoInput!) {
      saveHeldShareRebalanceLine(input: $input) {
        success
        message
        error
      }
    }`;

    // UpsertInput requires BOTH item and items (items: [X!]! is non-null).
    // Send an empty items array when saving a single record.
    const raw = await this.api.request<any>(gql, { input: { item, items: [] } });

    return {
      success: raw.saveHeldShareRebalanceLine?.success ?? false,
      message: raw.saveHeldShareRebalanceLine?.message ?? '',
      error: raw.saveHeldShareRebalanceLine?.error ?? '',
      data: item
    };
  }

  // ============================================================
  // DELETE
  // ============================================================
  async delete(id: number, hardDelete=false): Promise<ApiResponse<boolean>> {
    const gql = `mutation DeleteHeldShareRebalanceLine($id: Long!, $hardDelete: Boolean!) {
      deleteHeldShareRebalanceLine(id: $id, hardDelete: $hardDelete) {
        success
        message
        error
      }
    }`;

    const raw = await this.api.request<any>(gql, { id, hardDelete });

    return {
      success: raw.deleteHeldShareRebalanceLine?.success ?? false,
      message: raw.deleteHeldShareRebalanceLine?.message ?? '',
      error: raw.deleteHeldShareRebalanceLine?.error ?? '',
      data: raw.deleteHeldShareRebalanceLine?.success ?? false
    };
  }
}
